import { css } from '@emotion/react';
import styled from '@emotion/styled';
import {
  Button,
  PlainButton,
  TextButton,
  space,
} from 'folio-common-components';
import * as React from 'react';
import { OverlayMenu, menuLinkStyle } from '../OverlayMenu';
import type { ReceiptState } from '.';

const ReceiptMenuButton = styled(PlainButton)`
  ${menuLinkStyle};
`;

export const ReceiptMenu: React.FC<{
  state: ReceiptState;
  buttonType: 'secondary' | 'text';
  onSetReceiptState: (value: ReceiptState) => void;
}> = ({ state, buttonType, onSetReceiptState }) => {
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [justClosed, setJustClosed] = React.useState(false);
  const closeMenu = React.useCallback(() => {
    setIsMenuOpen(false);
    setJustClosed(true);
  }, []);

  React.useEffect(() => {
    if (justClosed) {
      // The button component is switched sometimes, so the ref is null when the
      // menu component tries to set its focus. This sets the focus manually to
      // the right button.
      buttonRef.current?.focus({ preventScroll: true });
      setJustClosed(false);
    }
  }, [justClosed]);

  const handleMenuItemClick = React.useCallback(
    (state: ReceiptState) => {
      onSetReceiptState(state);
      closeMenu();
    },
    [onSetReceiptState, closeMenu],
  );

  const notNeededItem = (
    <ReceiptMenuButton onClick={() => handleMenuItemClick('notNeeded')}>
      Trenger ikke kvittering
    </ReceiptMenuButton>
  );
  const lostItem = (
    <ReceiptMenuButton onClick={() => handleMenuItemClick('lost')}>
      Har mistet kvitteringen
    </ReceiptMenuButton>
  );

  let menuItems;
  switch (state) {
    case 'default':
      menuItems = (
        <>
          {notNeededItem}
          {lostItem}
        </>
      );
      break;

    case 'notNeeded':
      menuItems = (
        <>
          <ReceiptMenuButton onClick={() => handleMenuItemClick('default')}>
            Trenger kvittering likevel
          </ReceiptMenuButton>
          {lostItem}
        </>
      );
      break;

    case 'lost':
      menuItems = (
        <>
          {notNeededItem}
          <ReceiptMenuButton onClick={() => handleMenuItemClick('default')}>
            Kvittering ikke mistet likevel
          </ReceiptMenuButton>
        </>
      );
      break;
  }

  return (
    <span
      css={css`
        position: relative;
      `}
    >
      {buttonType === 'text' ? (
        <TextButton
          ref={buttonRef}
          aria-label="Valg for kvittering"
          onClick={() => setIsMenuOpen(true)}
        >
          Valg
        </TextButton>
      ) : (
        <Button
          ref={buttonRef}
          level="secondary"
          aria-label="Valg for kvittering"
          onClick={() => setIsMenuOpen(true)}
        >
          Valg
        </Button>
      )}
      <OverlayMenu
        isOpen={isMenuOpen}
        openButton={buttonRef}
        position="left"
        onClose={closeMenu}
        showCloseButton={false}
        css={css`
          min-width: 240px;
          top: auto;
          margin-top: 4px;
          ${space([16], 'padding')};

          @media not all and (min-width: 450px) {
            /* Hack to make sure the menu stays inside the viewport. We should
               have a proper way to do this. */
            left: -110px;
          }
        `}
      >
        {menuItems}
      </OverlayMenu>
    </span>
  );
};
